import { useEffect } from 'react';
import { useConnectivityStore } from '@/stores/connectivity-store';

const FRESH_API_MS = 30_000;

/**
 * Derives a robust online state — `navigator.onLine` alone lies. We treat the
 * kiosk as online only when at least two of three signals agree:
 *   1. navigator.onLine
 *   2. WS connected
 *   3. a successful API call within the last 30s
 */
export function useOnlineStatus(): boolean {
  const { online, wsConnected, lastApiSuccessAt, setOnline } = useConnectivityStore();

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, [setOnline]);

  // Hard signal: the OS says we're offline → trust it.
  if (!online) return false;

  const apiFresh = lastApiSuccessAt !== null && Date.now() - lastApiSuccessAt < FRESH_API_MS;
  const positives = [online, wsConnected, apiFresh].filter(Boolean).length;
  return positives >= 2;
}
